/**
 * One list per document type. A CREDIT NOTE is never listed as a return.
 *
 * The whole point of a separate list per type is that a customer can tell them
 * apart on paper. A shared "post-sale documents" list is how a return ends up
 * filed against the wrong thing, and it is the mistake 1.6.x made with
 * quotations appearing under Invoices.
 */
import { useMemo, useState } from 'react';
import { formatMoney } from '@core/money/format';
import { asMoney } from '@core/money/types';
import { returnTypeLabel, type ReturnDocType } from '@data/services/salesReturnService';
import { useReturnDocuments } from './useSalesReturns';

const statusLabel = (status: string) =>
  status === 'DRAFT'
    ? 'Draft'
    : status === 'ISSUED'
      ? 'Issued'
      : status[0] + status.slice(1).toLowerCase();

/** The route base each type lives under, so the two never collide. */
const BASE: Record<ReturnDocType, string> = {
  CREDIT_NOTE: '#/sales/credit-notes',
  SALES_RETURN: '#/sales/returns',
};

const rowTestId = (docType: ReturnDocType) =>
  docType === 'CREDIT_NOTE' ? 'credit-note-row' : 'sales-return-row';

export function ReturnListScreen({ docType }: { docType: ReturnDocType }) {
  const query = useReturnDocuments(docType);
  const [search, setSearch] = useState('');
  const base = BASE[docType];
  const label = returnTypeLabel(docType);
  const documents = useMemo(
    () =>
      (query.data?.documents ?? []).filter((document) => {
        const customer = query.data?.partyNamesByDocumentId.get(document.id) ?? '';
        return `${document.docNumber} ${document.docDate} ${customer} ${document.status}`
          .toLowerCase()
          .includes(search.trim().toLowerCase());
      }),
    [query.data, search],
  );

  return (
    <div className="flex min-h-full flex-col gap-4 p-4 text-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">{label}s</h1>
          <p className="text-xs text-slate-400">
            {docType === 'CREDIT_NOTE'
              ? 'A financial reduction against a valid sale. Issuing one credits the customer and never moves stock.'
              : 'Goods physically returned by the customer. Issuing one credits the customer and books the goods back into stock.'}
          </p>
        </div>
      </header>

      <section className="rounded border border-slate-800 p-3">
        <h2 className="mb-2 text-sm font-medium">
          Raise a {label.toLowerCase()} against an invoice
        </h2>
        <p className="text-xs text-slate-400">
          Open an invoice, then use “Create {label.toLowerCase()}”. A draft moves no stock and
          changes no balance.
        </p>
        <a
          data-testid="go-to-invoices"
          className="mt-2 inline-block rounded border border-slate-700 px-3 py-2 text-xs text-brand-300"
          href="#/sales/invoices"
        >
          Go to Invoices
        </a>
      </section>

      <input
        aria-label={`Search ${label.toLowerCase()}s`}
        data-testid="return-search"
        className="max-w-md rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
        placeholder="Search number, date, customer or status"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />

      {query.isLoading && <p className="text-sm text-slate-400">Loading…</p>}
      {query.isError && (
        <p role="alert" className="text-sm text-red-300">
          Could not load {label.toLowerCase()}s: {query.error.message}
        </p>
      )}
      {!query.isLoading && documents.length === 0 && (
        <p className="rounded border border-slate-800 p-4 text-sm text-slate-400">
          {search ? 'No matching documents.' : `No ${label.toLowerCase()}s yet.`}
        </p>
      )}

      <div className="overflow-x-auto rounded border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-left text-xs uppercase text-slate-400">
            <tr>
              <th className="p-3">{label}</th>
              <th className="p-3">Date</th>
              <th className="p-3">Customer</th>
              <th className="p-3 text-right">Credit value</th>
              <th className="p-3">Status</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {documents.map((document) => (
              <tr
                key={document.id}
                data-testid={rowTestId(docType)}
                data-doc-type={document.docType}
                className="border-t border-slate-800"
              >
                <td className="p-3 font-medium">{document.docNumber}</td>
                <td className="p-3">{document.docDate}</td>
                <td className="p-3">
                  {query.data?.partyNamesByDocumentId.get(document.id) ??
                    document.partySnapshot.name}
                </td>
                <td className="p-3 text-right">
                  {formatMoney(asMoney(document.grandTotalPaise))}
                </td>
                <td className="p-3">{statusLabel(document.status)}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-2">
                    <a className="text-brand-400 underline" href={`${base}/${document.id}`}>
                      Open / Preview
                    </a>
                    {document.status === 'DRAFT' && (
                      <a
                        className="text-brand-400 underline"
                        href={`${base}/${document.id}/edit`}
                      >
                        Edit draft
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        {documents.length} shown · {query.data?.total ?? 0} {label.toLowerCase()}s · invoices and
        receipts remain separate document types
      </p>
    </div>
  );
}